import { type Component, onMount, createSignal, Show } from 'solid-js';
import { Router, Route } from '@solidjs/router';
import { gameStore } from '@/stores/gameStore';
import { themeStore } from '@/stores/themeStore';
import { I18nProvider } from '@/i18n';
import LanguageSwitcher from '@/i18n/LanguageSwitcher';
import LoginForm from '@/components/LoginForm';
import Dashboard from '@/components/Dashboard';
import AdminDashboard from '@/components/AdminDashboard';
import AdminLogin from '@/components/AdminLogin';
import LoadingSpinner from '@/components/LoadingSpinner';
import NotificationToast from '@/components/NotificationToast';
import OfflineDetector from '@/components/OfflineDetector';
import AnimatedBackground from '@/components/layout/AnimatedBackground';

// Layout component that wraps all routes
const Layout: Component<{ children?: any }> = (props) => {
  return (
    <div class="app">
      <AnimatedBackground />
      <OfflineDetector />
      {/* Language switcher as fixed overlay only on login/non-dashboard pages */}
      <Show when={!gameStore.gameState.isAuthenticated || gameStore.gameState.isAdmin}>
        <div class="fixed top-4 right-4 z-50 flex items-center gap-2">
          <LanguageSwitcher />
        </div>
      </Show>
      <main class="main-content" role="main">
        {props.children}
      </main>
      <NotificationToast />
    </div>
  );
};

// Home page component with auth logic
const HomePage: Component = () => {
  return (
    <Show
      when={gameStore.gameState.isAuthenticated && !gameStore.gameState.isAdmin}
      fallback={<LoginForm />}
    >
      <Dashboard />
    </Show>
  );
};

// Admin page component with auth logic
const AdminPage: Component = () => {
  return (
    <Show
      when={gameStore.gameState.isAuthenticated && gameStore.gameState.isAdmin}
      fallback={<AdminLogin />}
    >
      <AdminDashboard />
    </Show>
  );
};

const App: Component = () => {
  const [isLoading, setIsLoading] = createSignal(true);

  onMount(() => {
    // Initialize theme system
    themeStore.initializeTheme();

    // Hide the initial loading screen
    const loadingEl = document.getElementById('loading');
    if (loadingEl) {
      setTimeout(() => {
        loadingEl.style.display = 'none';
        setIsLoading(false);
      }, 500);
    } else {
      setIsLoading(false);
    }
  });

  return (
    <I18nProvider>
      <Show when={!isLoading()} fallback={<LoadingSpinner />}>
        <Router root={Layout}>
          <Route path="/admin" component={AdminPage} />
          <Route path="/" component={HomePage} />
        </Router>
      </Show>
    </I18nProvider>
  );
};

export default App;
